import { CheckCircleOutlined } from '@ant-design/icons';
import { create } from 'zustand';

import DefaultToast from '@/components/Toast/DefaultToast';
import type { GLOBAL_SEARCH_VIEW } from '@/features/GlobalSearch/types/searchConst';
import service from '@/services/index';
import colors from '@/styles/colors/color';

import useToastStore from './pure/ToastStore';
import useUIStore, { getSystemTheme } from './pure/UIStore';

type State = {
  status: boolean;
  /**
   * @description
   * 현재 할당된 alias와 같은 alias일 경우에만 소켓에 의해 searchResult가 갱신됨
   */
  alias: string | null;
  searchResult: GlobalSearchResult | null;
  search_view_config: GLOBAL_SEARCH_VIEW[];
  notifyList: string[];
  histories: GlobalSearchResult[];
};

type Actions = {
  onSearchComplete: (alias: string, result: GlobalSearchResult) => void;
  setSearchStoreState: StoreSetter<State>;
  addSearchView: (view: GLOBAL_SEARCH_VIEW) => void;
  removeSearchView: (view: GLOBAL_SEARCH_VIEW) => void;
  syncSearchView: () => void;
  addToNotifyList: (alias: string) => void;
  removeFromNotifyList: (alias: string) => void;
  setHistories: (histories: GlobalSearchResult[]) => void;
  initSearchStore: () => void;
};

const initialState: State = {
  status: false,
  alias: null,
  searchResult: null,
  search_view_config: [],
  notifyList: [],
  histories: [],
};

const useGlobalSearchStore = create<State & Actions>((set, get) => ({
  ...initialState,
  onSearchComplete: (alias, result) => {
    const notifyList = get().notifyList;
    if (notifyList.includes(alias)) {
      const colorThemeSetting = useUIStore.getState().colorMode;
      const systemTheme = getSystemTheme();
      let color = colors;
      if (colorThemeSetting === 'system') {
        color = systemTheme === 'dark' ? colors : colors;
      } else {
        color = colorThemeSetting === 'dark' ? colors : colors;
      }

      useToastStore.getState().addToast(
        <DefaultToast
          header={`The analysis on '${result.title}' is complete.`}
          Icon={<CheckCircleOutlined style={{ color: color.green500 }} />}
          actionLabel="View"
          onClick={() => {
            set({
              status: true,
              alias,
            });
          }}
        />,
        {
          duration: 30000,
        },
      );
      get().removeFromNotifyList(alias);
    }

    if (get().alias === alias) {
      set({
        alias: alias,
        searchResult: result,
      });
    }

    set((prev) => {
      const newHistories = [...prev.histories];
      const targetIndex = newHistories.findIndex((v) => v.alias === alias);
      if (targetIndex !== -1) {
        newHistories.splice(targetIndex, 1, result);
        return {
          ...prev,
          histories: newHistories,
        };
      }
      return prev;
    });
  },

  async addSearchView(view: GLOBAL_SEARCH_VIEW) {
    await service.workspace.addSearchView(view);
    const newConfigs = [...get().search_view_config, view];
    set({
      search_view_config: newConfigs,
    });
  },

  async removeSearchView(view: GLOBAL_SEARCH_VIEW) {
    await service.workspace.deleteSearchView(view);
    const newConfigs = get().search_view_config.filter((v) => v !== view);
    set({
      search_view_config: newConfigs,
    });
  },

  async syncSearchView() {
    const data = await service.workspace.fetchSearchTabConfig();
    set({
      search_view_config: data.search_result_component,
    });
  },

  addToNotifyList: (alias) => {
    set((state) => ({
      notifyList: [...state.notifyList, alias],
    }));
  },
  removeFromNotifyList: (alias) => {
    set((state) => ({
      notifyList: state.notifyList.filter((v) => v !== alias),
    }));
  },
  setHistories: (histories) => {
    set({
      histories,
    });
  },
  initSearchStore() {
    set(initialState);
  },
  setSearchStoreState: set,
}));

export default useGlobalSearchStore;
